import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { XIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { useSitrepAutoRefresh } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useAorReportApprovalPolicy } from 'components/Reports/hooks/useAorReportApprovalPolicy';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import {
  maxIntervalAmountForUnit,
  minutesToInterval,
  validIntervalMinutes,
  type AutoRefreshIntervalUnit,
} from 'components/Reports/utils/sitrepAutoRefreshInterval';

type SitrepAutoRefreshControlsProps = {
  sitrep: GetSitrepsResponse;
  /**
   * When false, edits stay local until `save()` on the ref. Used by the draft
   * menu dialog with explicit Save / Cancel.
   */
  persistOnChange?: boolean;
};

export type SitrepAutoRefreshControlsHandle = {
  save: () => Promise<boolean>;
  isPending: boolean;
};

type NotifyRecipient =
  | { kind: 'user'; userGuid: string }
  | { kind: 'role'; roleId: string };

const recipientKey = (recipient: NotifyRecipient) =>
  recipient.kind === 'user'
    ? `user:${recipient.userGuid}`
    : `role:${recipient.roleId}`;

const sameRecipients = (left: NotifyRecipient[], right: NotifyRecipient[]) => {
  const keys = (items: NotifyRecipient[]) =>
    items.map(recipientKey).sort().join('|');
  return keys(left) === keys(right);
};

const formatLocalTime = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const time = new Date(value);
  if (!Number.isFinite(time.getTime())) return null;
  return time.toLocaleString();
};

const SitrepAutoRefreshControls = forwardRef<
  SitrepAutoRefreshControlsHandle,
  SitrepAutoRefreshControlsProps
>(({ sitrep, persistOnChange = true }, ref) => {
  const mutation = useSitrepAutoRefresh();
  const directory = useAorReportApprovalPolicy(sitrep.aor_id);
  const saved = sitrep.auto_refresh;
  const savedMinutes = saved?.intervalMinutes ?? 60;
  const savedRecipients = saved?.notifyRecipients ?? [];
  const initial = minutesToInterval(savedMinutes);
  const [enabled, setEnabled] = useState(Boolean(saved?.enabled));
  const [amount, setAmount] = useState(String(initial.amount));
  const [unit, setUnit] = useState<AutoRefreshIntervalUnit>(initial.unit);
  const [notifyRecipients, setNotifyRecipients] =
    useState<NotifyRecipient[]>(savedRecipients);

  // Re-sync local inputs when the saved schedule changes (after a save or another user's edit).
  const savedKey = `${Boolean(saved?.enabled)}:${savedMinutes}:${JSON.stringify(savedRecipients)}`;
  const [syncedKey, setSyncedKey] = useState(savedKey);
  if (syncedKey !== savedKey) {
    setSyncedKey(savedKey);
    setEnabled(Boolean(saved?.enabled));
    setAmount(String(initial.amount));
    setUnit(initial.unit);
    setNotifyRecipients(savedRecipients);
  }

  const users = directory.data?.users ?? [];
  const roles = directory.data?.roles ?? [];

  const recipientLabel = (recipient: NotifyRecipient) => {
    if (recipient.kind === 'user') {
      return (
        users.find((user) => user.userGuid === recipient.userGuid)
          ?.displayName ?? recipient.userGuid
      );
    }
    return (
      roles.find((role) => role.roleId === recipient.roleId)?.name ??
      `Role ${recipient.roleId}`
    );
  };

  const persist = async (
    nextEnabled: boolean,
    intervalMinutes: number,
    recipients: NotifyRecipient[] = notifyRecipients
  ) => {
    try {
      await mutation.mutateAsync({
        sitrepId: sitrep.id,
        enabled: nextEnabled,
        intervalMinutes,
        notifyRecipients: recipients,
      });
      return true;
    } catch {
      // Roll back the optimistic switch so the UI matches what the server has.
      setEnabled(Boolean(saved?.enabled));
      setNotifyRecipients(savedRecipients);
      toast.error('Could not save auto-refresh.');
      return false;
    }
  };

  const resolveIntervalMinutes = (
    nextAmount: string,
    nextUnit: AutoRefreshIntervalUnit
  ): number | null => validIntervalMinutes(Number(nextAmount), nextUnit);

  /** Save a changed interval; reverts the inputs when the value is not saveable. */
  const persistInterval = (
    nextAmount: string,
    nextUnit: AutoRefreshIntervalUnit
  ) => {
    const intervalMinutes = resolveIntervalMinutes(nextAmount, nextUnit);
    if (intervalMinutes === null) {
      toast.error(
        `Choose a whole number between 1 and ${maxIntervalAmountForUnit(nextUnit)} ${nextUnit}.`
      );
      const revert = minutesToInterval(savedMinutes);
      setAmount(String(revert.amount));
      setUnit(revert.unit);
      return;
    }
    if (intervalMinutes === savedMinutes && Boolean(saved?.enabled)) {
      return; // nothing changed
    }
    void persist(true, intervalMinutes);
  };

  const save = async (): Promise<boolean> => {
    const intervalMinutes =
      resolveIntervalMinutes(amount, unit) ?? (enabled ? null : savedMinutes);
    if (intervalMinutes === null) {
      toast.error(
        `Choose a whole number between 1 and ${maxIntervalAmountForUnit(unit)} ${unit}.`
      );
      return false;
    }
    const unchanged =
      Boolean(saved?.enabled) === enabled &&
      (!enabled || intervalMinutes === savedMinutes) &&
      sameRecipients(notifyRecipients, savedRecipients);
    if (unchanged) {
      return true;
    }
    return persist(enabled, intervalMinutes, notifyRecipients);
  };

  useImperativeHandle(
    ref,
    () => ({
      save,
      isPending: mutation.isPending,
    }),
    // save closes over latest amount/unit/enabled via this render
    [
      amount,
      unit,
      enabled,
      mutation.isPending,
      notifyRecipients,
      saved,
      savedMinutes,
      savedRecipients,
      sitrep.id,
    ]
  );

  const lastDraft = formatLocalTime(saved?.lastRunAt);
  const nextDraft = saved?.enabled ? formatLocalTime(saved?.nextRunAt) : null;
  const lastError = saved?.lastError?.trim() || null;
  const pausedAfterFailures =
    Boolean(lastError) &&
    !saved?.enabled &&
    (saved?.consecutiveFailures ?? 0) > 0;
  const intervalDisabled = !enabled || mutation.isPending;
  const timestampLabel = [
    nextDraft ? `Next draft: ${nextDraft}` : 'Next draft: pending',
    lastDraft ? `Last draft: ${lastDraft}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2"
      data-testid="sitrep-auto-refresh"
    >
      <div className="tw:flex tw:items-start tw:gap-2">
        <Switch
          id={`sitrep-auto-refresh-${sitrep.id}`}
          checked={enabled}
          disabled={mutation.isPending}
          className="tw:mt-0"
          onCheckedChange={(checked) => {
            const nextEnabled = checked === true;
            const intervalMinutes =
              resolveIntervalMinutes(amount, unit) ?? savedMinutes;
            setEnabled(nextEnabled);
            if (persistOnChange) {
              void persist(nextEnabled, intervalMinutes);
            }
          }}
        />
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
          <Label
            htmlFor={`sitrep-auto-refresh-${sitrep.id}`}
            className="tw:leading-5"
          >
            Auto-refresh this report
          </Label>
          <p className="tw:text-sm tw:leading-5 tw:font-normal tw:text-muted-foreground">
            Updates the in-progress draft for this incident or area on the
            interval, or creates one when none exists. Analytics and AI sections
            rerun. The draft stays in Drafts until you submit it.
          </p>
        </div>
      </div>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Label
          htmlFor={`sitrep-auto-refresh-amount-${sitrep.id}`}
          className="tw:sr-only"
        >
          Interval amount
        </Label>
        <Input
          id={`sitrep-auto-refresh-amount-${sitrep.id}`}
          type="number"
          min={1}
          max={maxIntervalAmountForUnit(unit)}
          step={1}
          className="tw:w-20"
          value={amount}
          disabled={intervalDisabled}
          onChange={(event) => setAmount(event.target.value)}
          onBlur={() => {
            if (persistOnChange && enabled) persistInterval(amount, unit);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
        />
        <Select
          value={unit}
          disabled={intervalDisabled}
          onValueChange={(next) => {
            const nextUnit = next === 'hours' ? 'hours' : 'minutes';
            setUnit(nextUnit);
            if (persistOnChange && enabled) persistInterval(amount, nextUnit);
          }}
        >
          <SelectTrigger
            size="sm"
            className="tw:w-28"
            aria-label="Interval unit"
          >
            <SelectValue>{unit === 'hours' ? 'hours' : 'minutes'}</SelectValue>
          </SelectTrigger>
          <SelectContent className="dark">
            <SelectItem value="minutes">minutes</SelectItem>
            <SelectItem value="hours">hours</SelectItem>
          </SelectContent>
        </Select>
        <span
          className={
            intervalDisabled
              ? 'tw:text-sm tw:text-muted-foreground tw:opacity-50'
              : 'tw:text-sm tw:text-muted-foreground'
          }
          aria-disabled={intervalDisabled}
        >
          {timestampLabel}
        </span>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Label>Notify when this draft is updated</Label>
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {notifyRecipients.map((recipient) => (
            <Badge
              key={recipientKey(recipient)}
              variant="secondary"
              className="tw:h-6 tw:rounded-full tw:pr-1"
            >
              {recipient.kind === 'role'
                ? `Role: ${recipientLabel(recipient)}`
                : recipientLabel(recipient)}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${recipientLabel(recipient)}`}
                onClick={() => {
                  const next = notifyRecipients.filter(
                    (item) => recipientKey(item) !== recipientKey(recipient)
                  );
                  setNotifyRecipients(next);
                  if (persistOnChange) {
                    const intervalMinutes =
                      resolveIntervalMinutes(amount, unit) ?? savedMinutes;
                    void persist(enabled, intervalMinutes, next);
                  }
                }}
              >
                <XIcon />
              </Button>
            </Badge>
          ))}
        </div>
        <SearchableSelect
          aria-label="Add a user to notify"
          placeholder="Add a user…"
          options={users.map((user) => ({
            value: user.userGuid,
            label: user.displayName,
          }))}
          value={null}
          onChange={(next) => {
            if (!next) return;
            const recipient: NotifyRecipient = {
              kind: 'user',
              userGuid: next.value,
            };
            if (
              notifyRecipients.some(
                (item) => recipientKey(item) === recipientKey(recipient)
              )
            ) {
              return;
            }
            const updated = [...notifyRecipients, recipient];
            setNotifyRecipients(updated);
            if (persistOnChange) {
              const intervalMinutes =
                resolveIntervalMinutes(amount, unit) ?? savedMinutes;
              void persist(enabled, intervalMinutes, updated);
            }
          }}
          positionerClassName="tw:z-[70]"
        />
        <SearchableSelect
          aria-label="Add a role to notify"
          placeholder="Add a role…"
          options={roles.map((role) => ({
            value: role.roleId,
            label: role.name,
          }))}
          value={null}
          onChange={(next) => {
            if (!next) return;
            const recipient: NotifyRecipient = {
              kind: 'role',
              roleId: next.value,
            };
            if (
              notifyRecipients.some(
                (item) => recipientKey(item) === recipientKey(recipient)
              )
            ) {
              return;
            }
            const updated = [...notifyRecipients, recipient];
            setNotifyRecipients(updated);
            if (persistOnChange) {
              const intervalMinutes =
                resolveIntervalMinutes(amount, unit) ?? savedMinutes;
              void persist(enabled, intervalMinutes, updated);
            }
          }}
          positionerClassName="tw:z-[70]"
        />
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          An empty list sends nothing. Roles are resolved to their current
          members when the draft is updated.
        </p>
      </div>
      {lastError ? (
        <p
          className="tw:text-sm tw:text-destructive"
          data-testid="sitrep-auto-refresh-error"
        >
          {pausedAfterFailures
            ? `Auto-refresh paused after ${saved?.consecutiveFailures} failed runs. Last error: ${lastError}`
            : `Last run failed: ${lastError}`}
        </p>
      ) : null}
    </div>
  );
});

SitrepAutoRefreshControls.displayName = 'SitrepAutoRefreshControls';

export default SitrepAutoRefreshControls;
